import { Permission } from '../../../backend/src/auth/permissions/permission';
import { useUserContext } from '../Contexts/User';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

/** Derive a human-readable message from a permission string like "subset:create" → "You do not have permission to create a subset" */
function defaultDisabledMessage(permission: string): string {
  const [resource, action] = permission.split(':');
  const resourceWords = resource
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim();
  const actionWords = action.replace(/-/g, ' ');
  const article = /^[aeiou]/i.test(resourceWords) ? 'an' : 'a';
  return `You do not have permission to ${actionWords} ${article} ${resourceWords}`;
}

export function useHasPermission() {
  const { user } = useUserContext();

  const hasPermission = (permission: Permission) => {
    return user?.role?.permissions?.includes(permission) ?? false;
  };

  return { hasPermission };
}

export function usePermission(permission: Permission) {
  const { user } = useUserContext();
  const allowed = user?.role?.permissions?.includes(permission) ?? false;
  return { allowed };
}

interface PermissionGateProps {
  permission: Permission | Permission[];
  orAllowed?: boolean;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  uiMode?: 'hidden' | 'disabled';
  disabledMessage?: string;
  requestSupport?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
}

export function PermissionGate({
  permission,
  orAllowed,
  children,
  fallback = null,
  uiMode = 'hidden',
  disabledMessage,
  requestSupport = false,
  side,
  sideOffset,
}: PermissionGateProps) {
  const permissions = Array.isArray(permission) ? permission : [permission];
  const { user } = useUserContext();
  const allowed = permissions.some((p) => user?.role?.permissions?.includes(p) ?? false);

  if (allowed || orAllowed) return <>{children}</>;
  if (uiMode === 'hidden') return <>{fallback}</>;

  // disabled mode
  const message =
    disabledMessage ??
    (permissions.length > 1
      ? 'You do not have permission to use this feature'
      : defaultDisabledMessage(permissions[0]));

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-not-allowed opacity-50">
          <div className="pointer-events-none">{children}</div>
        </div>
      </TooltipTrigger>
      <TooltipContent side={side} sideOffset={sideOffset}>
        <p>{message}</p>
        {requestSupport && (
          <p className="mt-1 text-xs opacity-75">
            Contact{' '}
            <a href="mailto:support@wexler.ai" className="underline">
              support@wexler.ai
            </a>{' '}
            to request access.
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
